import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * InDepthStructuredEditor
 *
 * Structure-preserving editor for the "Additional analysis" (In-depth analysis)
 * JSON dict. It renders the nested dict/array/string tree so users can edit the
 * text leaves without collapsing the structure that DigWatch renders. Structural
 * keys are treated as fixed labels (never renamed) so the DigWatch contract stays
 * stable; only values and list membership are editable.
 */

function updateAtPath(root, path, newVal) {
  if (path.length === 0) return newVal;
  const [head, ...rest] = path;
  if (Array.isArray(root)) {
    const copy = root.slice();
    copy[head] = updateAtPath(root[head], rest, newVal);
    return copy;
  }
  const copy = { ...(root || {}) };
  copy[head] = updateAtPath(root ? root[head] : undefined, rest, newVal);
  return copy;
}

function removeAtPath(root, path) {
  const parentPath = path.slice(0, -1);
  const lastKey = path[path.length - 1];
  const parent = parentPath.reduce((acc, k) => (acc ? acc[k] : undefined), root);
  if (Array.isArray(parent)) {
    const next = parent.filter((_, i) => i !== lastKey);
    return updateAtPath(root, parentPath, next);
  }
  if (parent && typeof parent === 'object') {
    const next = { ...parent };
    delete next[lastKey];
    return updateAtPath(root, parentPath, next);
  }
  return root;
}

function isPrimitive(v) {
  return v === null || typeof v !== 'object';
}

function textareaRows(value) {
  const len = (value || '').length;
  const byLines = (value || '').split('\n').length;
  return Math.min(16, Math.max(2, Math.max(byLines, Math.ceil(len / 90))));
}

function emptyTemplateFrom(sample) {
  if (typeof sample === 'string') return '';
  if (Array.isArray(sample)) return [];
  if (sample && typeof sample === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(sample)) {
      out[k] = typeof v === 'string' ? '' : Array.isArray(v) ? [] : v && typeof v === 'object' ? emptyTemplateFrom(v) : '';
    }
    return out;
  }
  return '';
}

const headingClassByDepth = (depth) => {
  if (depth <= 0) return 'text-base font-semibold text-gray-900';
  if (depth === 1) return 'text-sm font-semibold text-gray-800';
  return 'text-xs font-semibold uppercase tracking-wide text-gray-500';
};

function Node({ label, value, path, onChange, onRemove, depth }) {
  // String leaf
  if (typeof value === 'string') {
    return (
      <div>
        {label != null && (
          <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        )}
        <textarea
          value={value}
          onChange={(e) => onChange(path, e.target.value)}
          rows={textareaRows(value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
          placeholder={label ? `Enter ${String(label).toLowerCase()}...` : 'Enter text...'}
        />
      </div>
    );
  }

  if (isPrimitive(value)) {
    return (
      <div>
        {label != null && (
          <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        )}
        <input
          type="text"
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(path, e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    );
  }

  // Array
  if (Array.isArray(value)) {
    const allPrimitive = value.every(isPrimitive);
    return (
      <div className="space-y-2">
        {label != null && <div className={headingClassByDepth(depth)}>{label}</div>}
        <div className={`space-y-2 ${depth >= 0 ? 'border-l-2 border-gray-100 pl-3' : ''}`}>
          {value.map((item, idx) => (
            <div key={idx} className={allPrimitive ? '' : 'p-2 bg-gray-50 rounded-lg'}>
              {allPrimitive ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item == null ? '' : String(item)}
                    onChange={(e) => onChange([...path, idx], e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => onRemove ? onRemove([...path, idx]) : null}
                    className="p-1.5 text-gray-400 hover:text-red-600"
                    title="Remove"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-400">#{idx + 1}</span>
                    <button
                      type="button"
                      onClick={() => onRemove ? onRemove([...path, idx]) : null}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Remove item"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <Node
                    label={null}
                    value={item}
                    path={[...path, idx]}
                    onChange={onChange}
                    onRemove={onRemove}
                    depth={depth + 1}
                  />
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const template = value.length ? emptyTemplateFrom(value[0]) : '';
              onChange(path, [...value, template]);
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            <PlusIcon className="w-4 h-4" /> Add item
          </button>
        </div>
      </div>
    );
  }

  // Object
  const entries = Object.entries(value);
  return (
    <div className="space-y-3">
      {label != null && <div className={headingClassByDepth(depth)}>{label}</div>}
      <div className={depth >= 0 ? 'space-y-3 border-l-2 border-gray-100 pl-3' : 'space-y-3'}>
        {entries.map(([key, val]) => (
          <Node
            key={key}
            label={key}
            value={val}
            path={[...path, key]}
            onChange={onChange}
            onRemove={onRemove}
            depth={depth + 1}
          />
        ))}
      </div>
    </div>
  );
}

const InDepthStructuredEditor = ({ value, onChange }) => {
  const handleChange = (path, newVal) => {
    onChange(updateAtPath(value, path, newVal));
  };
  const handleRemove = (path) => {
    onChange(removeAtPath(value, path));
  };

  if (!value || typeof value !== 'object') {
    return null;
  }

  return (
    <div className="space-y-5">
      <p className="text-xs text-gray-500">
        Editing the structured In-depth analysis. Section names are fixed; edit the
        text and list items. Empty fields are removed on save.
      </p>
      {Object.entries(value).map(([key, val]) => (
        <div key={key} className="p-4 bg-white border border-gray-200 rounded-lg">
          <Node
            label={key}
            value={val}
            path={[key]}
            onChange={handleChange}
            onRemove={handleRemove}
            depth={0}
          />
        </div>
      ))}
    </div>
  );
};

export default InDepthStructuredEditor;
